// WebAuthn 브라우저 API wrapper — U64 (locker-pwa-passkey Phase 2.5)
// @simplewebauthn/browser를 래핑하여 에러를 한국어로 포장한 일관된 인터페이스 제공.
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';

// @simplewebauthn/browser v13은 @simplewebauthn/types를 내장한다.
// 별도 패키지 설치 없이 startRegistration / startAuthentication의 시그니처에서
// 파라미터·반환 타입을 추론하여 재사용한다.

/** startRegistration 첫 번째 인수 타입 — { optionsJSON: PublicKeyCredentialCreationOptionsJSON } */
type StartRegistrationOpts = Parameters<typeof startRegistration>[0];
/** startAuthentication 첫 번째 인수 타입 — { optionsJSON: PublicKeyCredentialRequestOptionsJSON } */
type StartAuthenticationOpts = Parameters<typeof startAuthentication>[0];

/** 등록 옵션 JSON (서버 → 브라우저 전달 형식) */
export type PublicKeyCredentialCreationOptionsJSON =
  StartRegistrationOpts['optionsJSON'];
/** 인증 옵션 JSON (서버 → 브라우저 전달 형식) */
export type PublicKeyCredentialRequestOptionsJSON =
  StartAuthenticationOpts['optionsJSON'];
/** 등록 응답 JSON (브라우저 → 서버 전달 형식) */
export type RegistrationResponseJSON = Awaited<ReturnType<typeof startRegistration>>;
/** 인증 응답 JSON (브라우저 → 서버 전달 형식) */
export type AuthenticationResponseJSON = Awaited<ReturnType<typeof startAuthentication>>;

// ─── 커스텀 에러 타입 ─────────────────────────────────────────

/** WebAuthn 자체를 지원하지 않는 환경에서 throw */
export class WebauthnNotSupportedError extends Error {
  constructor() {
    super('이 브라우저는 WebAuthn을 지원하지 않습니다.');
    this.name = 'WebauthnNotSupportedError';
  }
}

/** 사용자가 패스키 프롬프트를 취소·거부했을 때 throw */
export class WebauthnUserCancelledError extends Error {
  constructor(message = '사용자가 패스키 요청을 취소했습니다.') {
    super(message);
    this.name = 'WebauthnUserCancelledError';
  }
}

/** 기기/인증기 오류(타임아웃, 보안 에러 등) 때 throw */
export class WebauthnDeviceError extends Error {
  /** 브라우저가 던진 원본 에러 */
  readonly cause: unknown;
  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'WebauthnDeviceError';
    this.cause = cause;
  }
}

// ─── 능력 감지 ────────────────────────────────────────────────

/** 브라우저 WebAuthn 지원 여부와 플랫폼 인증기 가용성 */
export interface WebauthnCapabilities {
  /** navigator.credentials API 지원 여부 */
  available: boolean;
  /** Touch ID / 지문 / 얼굴 인식 등 디바이스 내장 인증기 지원 여부 */
  platformAuthenticator: boolean;
}

/**
 * 현재 브라우저·기기의 WebAuthn 능력을 감지한다.
 *
 * - available: navigator.credentials && window.PublicKeyCredential 존재 여부
 * - platformAuthenticator: isUserVerifyingPlatformAuthenticatorAvailable() 호출
 */
export async function detectCapabilities(): Promise<WebauthnCapabilities> {
  // navigator.credentials 또는 PublicKeyCredential 미존재 → 미지원
  if (
    typeof navigator === 'undefined' ||
    !navigator.credentials ||
    typeof window === 'undefined' ||
    !window.PublicKeyCredential
  ) {
    return { available: false, platformAuthenticator: false };
  }

  let platformAuthenticator = false;
  try {
    platformAuthenticator =
      await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    // 일부 환경에서 API 존재하지만 호출 실패 — false 처리
    platformAuthenticator = false;
  }

  return { available: true, platformAuthenticator };
}

// ─── 패스키 등록 ──────────────────────────────────────────────

/**
 * 서버에서 받은 등록 옵션으로 패스키를 등록한다.
 *
 * @param options - 서버가 /api/passkeys/register/start 에서 반환한 JSON 옵션
 * @returns 서버의 /api/passkeys/register/finish 에 전달할 등록 응답
 * @throws {WebauthnNotSupportedError} WebAuthn 미지원 환경
 * @throws {WebauthnUserCancelledError} 사용자 취소 / 거부
 * @throws {WebauthnDeviceError} 그 외 인증기·브라우저 오류
 */
export async function registerPasskey(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  // WebAuthn 지원 여부 사전 확인
  if (!navigator.credentials || !window.PublicKeyCredential) {
    throw new WebauthnNotSupportedError();
  }

  try {
    return await startRegistration({ optionsJSON: options });
  } catch (err) {
    throw mapBrowserError(err);
  }
}

// ─── 패스키 인증 ──────────────────────────────────────────────

/**
 * 서버에서 받은 인증 옵션으로 패스키 인증을 수행한다.
 *
 * @param options - 서버가 /api/passkeys/auth/start 에서 반환한 JSON 옵션
 * @returns 서버의 /api/passkeys/auth/finish 에 전달할 인증 응답
 * @throws {WebauthnNotSupportedError} WebAuthn 미지원 환경
 * @throws {WebauthnUserCancelledError} 사용자 취소 / 거부
 * @throws {WebauthnDeviceError} 그 외 인증기·브라우저 오류
 */
export async function authenticatePasskey(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  if (!navigator.credentials || !window.PublicKeyCredential) {
    throw new WebauthnNotSupportedError();
  }

  try {
    return await startAuthentication({ optionsJSON: options });
  } catch (err) {
    throw mapBrowserError(err);
  }
}

// ─── 내부 헬퍼 ────────────────────────────────────────────────

/**
 * 브라우저/인증기가 던진 원시 에러를 도메인 에러로 변환한다.
 * DOMException의 name을 먼저 확인한 후 일반 Error를 처리한다.
 * jsdom 등 일부 환경에서 DOMException이 Error를 상속하지 않을 수 있으므로
 * instanceof 대신 name 속성 존재를 직접 검사한다.
 *
 * NotAllowedError: 사용자 취소 또는 제스처 없음
 * SecurityError: 잘못된 origin
 * AbortError: 타임아웃 또는 명시적 abort
 * 그 외: WebauthnDeviceError
 */
function mapBrowserError(err: unknown): Error {
  // err가 name 속성을 가진 객체라면 DOMException 또는 Error 계열로 처리
  if (err !== null && typeof err === 'object' && 'name' in err) {
    const errObj = err as { name: string; message?: string };
    const name = errObj.name;
    const message = errObj.message ?? '';

    // 사용자가 직접 취소하거나 사용자 제스처 없이 호출된 경우
    if (name === 'NotAllowedError') {
      return new WebauthnUserCancelledError(
        '패스키 요청이 거부되었습니다. 사용자가 취소했거나 권한이 없습니다.',
      );
    }

    // 타임아웃 또는 abort()로 중단된 경우
    if (name === 'AbortError') {
      return new WebauthnUserCancelledError(
        '패스키 요청이 시간 초과되었거나 중단되었습니다.',
      );
    }

    // origin 불일치 등 보안 정책 위반
    if (name === 'SecurityError') {
      return new WebauthnDeviceError(
        '보안 정책 오류: 허용되지 않은 origin에서의 WebAuthn 요청입니다.',
        err,
      );
    }

    // 기기에 적합한 인증기가 없음
    if (name === 'NotSupportedError') {
      return new WebauthnDeviceError(
        '이 기기에서 사용 가능한 인증기를 찾을 수 없습니다.',
        err,
      );
    }

    // 자격증명 이미 존재 등 제약 위반
    if (name === 'ConstraintError' || name === 'InvalidStateError') {
      return new WebauthnDeviceError(
        `인증기 상태 오류: ${message}`,
        err,
      );
    }

    // 기타 알 수 없는 에러 — message가 있으면 포함
    return new WebauthnDeviceError(
      message ? `WebAuthn 오류: ${message}` : '알 수 없는 WebAuthn 오류가 발생했습니다.',
      err,
    );
  }

  // 객체조차 아닌 경우
  return new WebauthnDeviceError('알 수 없는 WebAuthn 오류가 발생했습니다.', err);
}
